import { useRouter } from "next/router";
import { useEffect } from "react";

import Button from "@/blueprint/components/Button/Button";
import { CloseIcon } from "@/consent/ConsentIcons";
import ConsentPreferencesDialog from "@/consent/ConsentPreferencesDialog";
import TextLink from "@/consent/TextLink";
import { CONSENT_BANNER } from "@/consent/consentContent";
import { useConsentStore } from "@/consent/useConsentStore";
import { announce } from "@/hooks/useAnnounce";
import { cn } from "@/lib/utils";

// Single-purpose pages where the banner would sit over the one thing on them.
// Consent stays at its defaults there; the banner shows on the next page.
const BANNERLESS_PATHS: ReadonlySet<string> = new Set(["/wsj"]);

// Deliberately below the sweepstakes modal (z-[60]): its overlay covers the
// banner and aria-modal makes it inert until the modal closes.
export default function ConsentBanner() {
  const bannerOpen = useConsentStore((s) => s.bannerOpen);
  const initBanner = useConsentStore((s) => s.initBanner);
  const acceptAll = useConsentStore((s) => s.acceptAll);
  const dismissBanner = useConsentStore((s) => s.dismissBanner);
  const openPrefs = useConsentStore((s) => s.openPrefs);
  const { pathname } = useRouter();
  const isBannerless = BANNERLESS_PATHS.has(pathname);

  useEffect(() => {
    if (isBannerless) return;
    initBanner();
    // The banner sits last in the tab order; tell screen-reader users it exists.
    if (useConsentStore.getState().bannerOpen) {
      announce(CONSENT_BANNER.announcement);
    }
  }, [initBanner, isBannerless]);

  return (
    <>
      {bannerOpen && !isBannerless && (
        <section
          aria-label={CONSENT_BANNER.regionLabel}
          className={cn(
            "fixed z-50 flex w-[340px] flex-col rounded-24 bg-brand-white shadow-card-pop",
            // Viewport offsets match the newsletter popup (sonner defaults).
            "inset-x-m bottom-m mx-auto p-m",
            "tab:bottom-l tab:left-l tab:right-auto tab:mx-0 tab:w-[360px] tab:p-20",
            "sd:p-l",
            "d:rounded-32 d:p-xl",
            // duration-* only drives transitions, not animations.
            "animate-in slide-in-from-bottom-[150%] [animation-duration:2000ms] [animation-timing-function:ease-out] motion-reduce:animate-none",
          )}
        >
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={CONSENT_BANNER.dismiss}
              onClick={dismissBanner}
              className="relative size-24 text-content-primary after:absolute after:-inset-[10px] after:content-['']"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="mt-[4px] flex flex-col gap-20 tab:mt-0 tab:gap-m sd:gap-20 d:gap-l">
            <div className="flex flex-col gap-s">
              <p className="text-center font-ppMori text-form-label font-semibold text-content-primary">
                {CONSENT_BANNER.title}
              </p>
              <p className="text-center font-ppMori text-paragraph-sm text-content-primary">
                {CONSENT_BANNER.body}
              </p>
            </div>
            {/* Accepting is the only one-tap choice here; declining lives in
                the settings dialog, whose first tier is necessary-only. */}
            <div className="flex flex-col items-center gap-[4px]">
              <Button size="md" onClick={acceptAll} className="h-[50px] w-full">
                {CONSENT_BANNER.acceptAll}
              </Button>
              <TextLink onClick={openPrefs}>{CONSENT_BANNER.settings}</TextLink>
            </div>
          </div>
        </section>
      )}
      <ConsentPreferencesDialog />
    </>
  );
}
